import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapTrifold, AirplaneTilt, Buildings, Car, Confetti, Train, Percent, Megaphone } from "@phosphor-icons/react";
import { api, inr } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import HeroSlider from "@/components/HeroSlider";
import WhyChooseUs from "@/components/WhyChooseUs";
import PackageCard from "@/components/PackageCard";
import InquiryForm from "@/components/InquiryForm";
import GoogleReviews from "@/components/GoogleReviews";
import { SERVICES, DEST_CARDS, INTL_CARDS, PROMOS } from "@/data/site";

const ICONS = { MapTrifold, AirplaneTilt, Buildings, Car, Confetti, Train };

function TwoTone({ orange, navy }) {
  return (
    <h2 className="text-4xl sm:text-5xl font-heading font-semibold leading-tight">
      <span className="text-brand-orange">{orange}</span> <span className="text-brand-navy">{navy}</span>
    </h2>
  );
}

function DestinationCard({ card, price, index }) {
  return (
    <Reveal delay={(index % 4) * 0.06}>
      <Link
        to={`/packages?q=${encodeURIComponent(card.match)}`}
        data-testid={`dest-card-${card.match.toLowerCase().replace(/\s+/g, "-")}`}
        className="group relative block overflow-hidden rounded-2xl aspect-[3/4] shadow-[0_8px_30px_rgba(46,42,128,0.12)]"
      >
        <img
          src={card.image}
          alt={`${card.prefix} ${card.name}`}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/90 via-brand-navy/25 to-transparent" />
        <div className="absolute bottom-0 inset-x-0 p-5 transition-transform duration-300 group-hover:-translate-y-1.5">
          <p className="font-heading italic text-white/85 text-sm">{card.prefix}</p>
          <p className="font-heading text-2xl font-semibold text-white leading-tight">{card.name}</p>
          {price && (
            <p className="mt-1.5 text-yellow-400 font-accent font-extrabold text-sm uppercase tracking-wide">
              Starting {inr(price)}/-
            </p>
          )}
        </div>
      </Link>
    </Reveal>
  );
}

export default function Home() {
  const [packages, setPackages] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [offers, setOffers] = useState([]);

  useEffect(() => {
    api.get("/packages").then((r) => {
      setPackages(r.data);
      const feat = r.data.filter((p) => p.featured);
      setFeatured((feat.length >= 6 ? feat : r.data).slice(0, 6));
    }).catch(() => {});
    api.get("/offers").then((r) => setOffers(r.data)).catch(() => {});
  }, []);

  const minPrice = useMemo(() => {
    const map = {};
    for (const p of packages) {
      const key = p.country_state || "";
      for (const card of [...DEST_CARDS, ...INTL_CARDS]) {
        const m = card.match.toLowerCase();
        if (key.toLowerCase().includes(m) || m.includes(key.toLowerCase() || "~")) {
          if (!map[card.match] || p.price < map[card.match]) map[card.match] = p.price;
        }
      }
    }
    return map;
  }, [packages]);

  return (
    <div>
      <HeroSlider />

      {/* SEASONAL OFFERS */}
      {offers.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 md:px-10 pt-10" data-testid="seasonal-offers">
          <div className="space-y-4">
            {offers.slice(0, 3).map((o, i) => (
              <Reveal key={o.id} delay={i * 0.08}>
                <Link
                  to={`/packages${o.link_query ? `?q=${encodeURIComponent(o.link_query)}` : ""}`}
                  data-testid={`offer-banner-${o.id}`}
                  className="group relative flex flex-col sm:flex-row items-center gap-4 sm:gap-6 overflow-hidden rounded-2xl bg-gradient-to-r from-yellow-400 via-amber-400 to-brand-orange p-5 sm:p-6 shadow-[0_12px_36px_rgba(240,128,32,0.35)] transition-transform hover:-translate-y-1"
                >
                  <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-brand-navy text-yellow-400">
                    <Megaphone size={28} weight="fill" />
                  </span>
                  <div className="flex-1 text-center sm:text-left">
                    <p className="font-heading text-2xl sm:text-3xl font-bold text-brand-navy leading-tight">{o.title}</p>
                    {o.subtitle && <p className="mt-0.5 text-sm text-brand-navy/75 font-body">{o.subtitle}</p>}
                  </div>
                  {o.discount_text && (
                    <span className="shrink-0 rounded-full bg-white px-5 py-2.5 font-accent font-extrabold text-brand-orange text-lg shadow-md rotate-[-2deg] group-hover:rotate-0 transition-transform">
                      {o.discount_text}
                    </span>
                  )}
                  <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-brand-navy px-6 py-3 text-xs font-extrabold uppercase tracking-wider text-white">
                    Grab Offer <ArrowRight size={14} weight="bold" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* POPULAR DOMESTIC TOURS */}
      <section className="py-16 md:py-20" data-testid="popular-tours">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Reveal className="text-center mb-12">
            <TwoTone orange="Explore" navy="Popular Tours!" />
            <p className="mt-3 text-[#6b6a8a] font-body">Destinations worth exploring! Here are a few popular spots</p>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {DEST_CARDS.map((c, i) => (
              <DestinationCard key={c.match} card={c} price={minPrice[c.match]} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* PROMO BANNERS */}
      <section className="pb-16 md:pb-20" data-testid="promo-banners">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PROMOS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.06}>
              <Link
                to={`/packages?q=${encodeURIComponent(p.q)}`}
                data-testid={`promo-${i}`}
                className={`group relative block overflow-hidden rounded-2xl bg-gradient-to-br ${p.grad} p-6 text-white shadow-lg transition-transform hover:-translate-y-1`}
              >
                <span className="inline-flex items-center gap-1 rounded-full bg-yellow-400 px-3 py-1 text-[11px] font-extrabold uppercase tracking-wider text-brand-navy">
                  <Percent size={12} weight="bold" /> Special Offer
                </span>
                <h3 className="mt-4 font-heading text-2xl font-semibold text-white">{p.title}</h3>
                <p className="mt-1 font-accent font-bold text-yellow-300">{p.offer}</p>
                <p className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-white/90 group-hover:gap-3 transition-all">
                  Discover More <ArrowRight size={14} weight="bold" />
                </p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INTERNATIONAL TOURS */}
      <section className="py-16 md:py-20 bg-brand-navy relative overflow-hidden grain" data-testid="international-tours">
        <div className="max-w-7xl mx-auto px-5 md:px-10 relative z-10">
          <Reveal className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-heading font-semibold leading-tight">
              <span className="text-brand-orange">Explore</span> <span className="text-white">International Tours!</span>
            </h2>
            <p className="mt-3 text-white/70 font-body">Fly beyond borders with our best-value international escapes</p>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
            {INTL_CARDS.map((c, i) => (
              <Reveal key={c.match} delay={(i % 5) * 0.06}>
                <Link
                  to={`/packages?q=${encodeURIComponent(c.match)}`}
                  data-testid={`intl-card-${c.match.toLowerCase()}`}
                  className="group relative block overflow-hidden rounded-2xl aspect-[3/4] shadow-lg"
                >
                  <img src={c.image} alt={c.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 inset-x-0 p-4">
                    <p className="font-heading text-xl text-white"><span className="italic text-brand-orange">Explore</span> {c.name}</p>
                    {minPrice[c.match] && (
                      <p className="mt-1 text-yellow-400 font-accent font-bold text-xs uppercase tracking-wide">Starting At : {inr(minPrice[c.match])}/-</p>
                    )}
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BEST TOUR PACKAGES */}
      <section className="py-16 md:py-20" data-testid="best-packages">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Reveal className="text-center mb-12">
            <TwoTone orange="Explore Best" navy="Tour Packages!" />
            <p className="mt-3 text-[#6b6a8a] font-body">Hotel stay · Meals · Sightseeing · Private cab — all included</p>
          </Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {featured.map((p, i) => <PackageCard key={p.id} pkg={p} index={i} />)}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/packages"
              data-testid="view-all-packages"
              className="inline-flex items-center gap-2 rounded-full bg-brand-teal px-8 py-3.5 text-sm font-bold uppercase tracking-wide text-white transition-transform hover:-translate-y-0.5"
            >
              View All Packages <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* SPECIAL OFFER STRIP */}
      <section className="relative overflow-hidden bg-gradient-to-r from-brand-orange to-amber-500 py-14" data-testid="special-offer">
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div>
            <p className="inline-block rounded-full bg-white/20 px-4 py-1 text-xs font-extrabold uppercase tracking-[0.2em] text-white">Limited Time</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-5xl font-bold text-white">Get 10% OFF on Group Bookings!</h2>
            <p className="mt-2 text-white/85 font-body">Book for 6+ travellers and unlock instant savings on any package.</p>
          </div>
          <Link
            to="/contact"
            data-testid="offer-cta"
            className="shrink-0 rounded-full bg-brand-navy px-9 py-4 text-sm font-extrabold uppercase tracking-wider text-white shadow-xl transition-transform hover:-translate-y-0.5"
          >
            Get Special Offer
          </Link>
        </div>
      </section>

      <WhyChooseUs />

      {/* SERVICES */}
      <section id="services" className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-20" data-testid="services-section">
        <Reveal className="text-center mb-12">
          <TwoTone orange="Our" navy="Services" />
          <p className="mt-3 text-[#6b6a8a] font-body">Everything your journey needs, under one roof</p>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon] || MapTrifold;
            return (
              <Reveal key={s.title} delay={(i % 3) * 0.08}>
                <div className="group h-full rounded-2xl bg-white p-7 shadow-[0_8px_30px_rgba(46,42,128,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(46,42,128,0.16)]" data-testid={`service-${i}`}>
                  <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-brand-teal/10 text-brand-teal transition-colors group-hover:bg-brand-teal group-hover:text-white">
                    <Icon size={28} weight="duotone" />
                  </span>
                  <h3 className="mt-5 text-xl font-heading font-semibold text-brand-navy">{s.title}</h3>
                  <p className="mt-2 text-sm text-[#6b6a8a] font-body leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <GoogleReviews />

      {/* CTA + FORM */}
      <section className="bg-brand-navy py-20 md:py-24 relative overflow-hidden grain" data-testid="home-cta">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-14 items-center relative z-10">
          <div>
            <h2 className="text-4xl sm:text-5xl font-heading font-semibold text-white leading-tight">
              Let's design your next <span className="italic text-yellow-400">great escape.</span>
            </h2>
            <p className="mt-5 text-white/70 font-body max-w-md leading-relaxed">
              Share a few details and our travel expert will craft a personalised itinerary with the best prices — no obligation.
            </p>
          </div>
          <div className="rounded-3xl bg-white p-7 md:p-9 shadow-2xl">
            <InquiryForm compact />
          </div>
        </div>
      </section>
    </div>
  );
}
